import {
  ACTION_COMPLETED_KEY,
  COUNTDOWN_SECONDS,
  COUNTDOWN_START_KEY,
} from "constants/sessionTimeout";
import { useCallback, useEffect, useRef, useState } from "react";

function useHandlerBeforeSessionTimeout(action: () => void) {
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [actionExecuted, setActionExecuted] = useState(false);

  const calculateRemainingTime = useCallback(() => {
    const startTimestamp = localStorage.getItem(COUNTDOWN_START_KEY);
    if (!startTimestamp) {
      return COUNTDOWN_SECONDS;
    }
    const startTime = parseInt(startTimestamp, 10);
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const remaining = Math.max(0, COUNTDOWN_SECONDS - elapsed);
    return remaining;
  }, []);

  const updateCountdown = useCallback(async () => {
    const remaining = calculateRemainingTime();
    setCountdown(remaining);
    if (remaining <= 1) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (!actionExecuted) {
        localStorage.setItem(ACTION_COMPLETED_KEY, "pending");
        setActionExecuted(true);
        await action();
        localStorage.removeItem(ACTION_COMPLETED_KEY);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateRemainingTime, action]);

  useEffect(() => {
    setCountdown(COUNTDOWN_SECONDS);

    updateCountdown();

    countdownIntervalRef.current = setInterval(() => {
      updateCountdown();
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [updateCountdown]);

  return { countdown };
}

export { useHandlerBeforeSessionTimeout };
